// Shared helper for posting lead-capture events to GoHighLevel (GHL) inbound
// webhooks. There are separate endpoints per event on purpose (per Tracy) —
// each event drives its own GHL workflow / text notification to home
// advisors, so they shouldn't share one URL.
//
// Suggested location: src/utils/ghlWebhooks.js (sibling to your other
// utils/helpers — adjust the import path in HomePortalPage.js if your
// project structure differs).
//
// TODO: paste your real GHL inbound webhook URLs below. In GHL:
//   Automation → Workflows → (your workflow) → Add Trigger → "Inbound Webhook"
//   → copy the generated Webhook URL.
// Until you do, calls are safely skipped (logged to console, nothing sent).

const GHL_WEBHOOK_HOME_CLAIMED = "https://services.leadconnectorhq.com/hooks/bZI6dyyFAn2ZSD9YUdpT/webhook-trigger/403dabfd-cb5a-477e-8d06-a4ac63ae7ba6";
const GHL_WEBHOOK_HEALTH_CHECK_COMPLETED = "https://services.leadconnectorhq.com/hooks/bZI6dyyFAn2ZSD9YUdpT/webhook-trigger/7091f9a5-6099-4828-a86e-e05259da24ab";
const GHL_WEBHOOK_ACCOUNT_CREATED = "https://services.leadconnectorhq.com/hooks/bZI6dyyFAn2ZSD9YUdpT/webhook-trigger/dbfd0d8f-5e13-417b-adc3-7147e5a47fcf";
// TODO: create a new GHL workflow with its own Inbound Webhook trigger for
// this event, then paste the generated URL here. Until you do, calls are
// safely skipped (logged to console, nothing sent) — same as any other
// placeholder URL in this file.
const GHL_WEBHOOK_VALUE_TRACKER_CREATED = "https://services.leadconnectorhq.com/hooks/bZI6dyyFAn2ZSD9YUdpT/webhook-trigger/bbc3b732-c85a-4bb0-abca-5e59a1ba24d3";
const GHL_WEBHOOK_VALUE_TRACKER_INTEREST = "https://services.leadconnectorhq.com/hooks/bZI6dyyFAn2ZSD9YUdpT/webhook-trigger/e7fd2f39-48aa-411e-aa1c-33ad2f33c56f";

// Fire-and-forget POST helper. Never throws — a GHL/webhook outage should
// never block someone from moving forward in the funnel. Returns true/false
// only so a caller *could* show a subtle indicator; nothing should gate on it.
async function postToGhl(url, payload) {
  if (!url || url.includes("REPLACE_WITH")) {
    console.warn("[ghlWebhooks] Skipped — webhook URL is still a placeholder. Payload was:", payload);
    return false;
  }
  try {
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return resp.ok;
  } catch (err) {
    console.error("[ghlWebhooks] POST failed:", err);
    return false;
  }
}

// Fired from HomePortalPage.js the instant a visitor confirms (or
// self-reports) their identity on the "Is this you?" step — BEFORE they've
// necessarily created an account. This lets home advisors follow up on
// partial leads who bail before finishing signup.
//
// `email` is optional and usually null — the claim-your-home flow asks for
// it but never requires it, to keep friction low at that step. GHL's
// Create/Update Contact action needs at least a phone or email to make a
// real Contact record, so a null email here means this event can still
// only drive an Internal Notification, not a mailing-list-ready Contact —
// that's expected, not a bug. Every claim WITH an email, though, is a real
// address+email lead you can build a mailing list from over time.
export function sendHomeClaimedWebhook({
  firstName,
  lastName,
  ownerMatch,        // true = matched the public-record owner name we showed them, false = self-reported / edited
  matchedOwnerName,  // the public-record name we displayed, if any (useful for QA / spotting mismatches)
  email,             // optional — null when the visitor skipped it
  address,
  city,
  state,
  zipCode,
  yearBuilt,
  squareFeet,
  bedrooms,
  bathrooms,
  claimSource,       // "logged_in_portal" (HomeBuddha.js, existing member) vs "anonymous_visitor"
                      // (HomePortalPage.js, "Is this you?" flow). Lets the Internal Notification
                      // message show whether this was an already-registered user confirming their
                      // home vs. a brand-new visitor lead — add the merge field
                      // {{ Inbound Webhook Trigger . Claim Source }} to that message once GHL has
                      // detected it from a fresh sample payload.
}) {
  return postToGhl(GHL_WEBHOOK_HOME_CLAIMED, {
    event: "home_claimed",
    firstName,
    lastName,
    ownerMatch,
    matchedOwnerName: matchedOwnerName || null,
    email: email || null,
    address,
    city,
    state,
    zipCode,
    yearBuilt,
    squareFeet,
    bedrooms,
    bathrooms,
    claimSource: claimSource || "anonymous_visitor",
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
  });
}

// Call this from HomeBuddha.js when the Home Health Check-Up quiz is
// submitted/completed. Pass along whatever signals selling intent (e.g. a
// "thinking about selling?" quiz question) so GHL can route hot leads to
// advisors — this is the "who might be selling" signal Tracy wants.
//
// Not wired in yet — HomeBuddha.js / the quiz-submit handler wasn't part of
// this file set. Import this function there and call it where the quiz
// result is finalized, e.g.:
//
//   import { sendHealthCheckCompletedWebhook } from "../utils/ghlWebhooks";
//   ...
//   sendHealthCheckCompletedWebhook({
//     firstName, lastName, address, city, state, zipCode,
//     healthScore: score,
//     sellingInterest: answers.sellingInterest, // whatever field name your quiz uses
//     answers,
//   });
export function sendHealthCheckCompletedWebhook({
  firstName,
  lastName,
  address,
  city,
  state,
  zipCode,
  healthScore,
  sellingInterest,   // e.g. "yes" / "maybe" / "no" / "within 12 months" — however your quiz captures it
  answers,           // full answer set, optional — include if you want raw data visible in GHL
  // The quiz no longer requires an account — `anonymous: true` means
  // firstName/lastName/email-equivalent fields will be blank (there's no
  // Contact identity yet, so GHL's Create/Update Contact action can't build
  // a real Contact from this call — expect Internal Notification only,
  // same caveat as the null-email case on sendHomeClaimedWebhook above).
  // `convertedFromAnonymous: true` marks the follow-up call fired once that
  // same visitor creates an account and their stashed results get saved for
  // real — build a separate branch/workflow off that flag if you want to
  // message these people differently ("welcome back, here's what you found").
  anonymous,
  convertedFromAnonymous,
}) {
  return postToGhl(GHL_WEBHOOK_HEALTH_CHECK_COMPLETED, {
    event: "health_check_completed",
    firstName,
    lastName,
    address,
    city,
    state,
    zipCode,
    healthScore,
    sellingInterest: sellingInterest || null,
    answers: answers || null,
    anonymous: !!anonymous,
    convertedFromAnonymous: !!convertedFromAnonymous,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
  });
}

// Fired from ValueTracker.js the moment an ANONYMOUS (not-yet-logged-in)
// visitor enters an address, we pull an AVM for it (or try to), and they
// click through past the confirm step — BEFORE they log in or create an
// account. This is the Value Tracker equivalent of sendHomeClaimedWebhook:
// capture the address (and rough value, if the lookup succeeded) even if
// this person never finishes signing up.
//
// No name/email/phone here — this step deliberately only asks for an
// address to keep friction as low as possible. Adding an optional email
// ask to this step (mirroring the "local discounts" ask on the
// claim-your-home flow) would be a follow-up, not built in yet.
export function sendValueTrackerInterestWebhook({
  address,
  avm,       // AVM estimate pulled for this address, if the lookup succeeded
  avmLow,
  avmHigh,
}) {
  return postToGhl(GHL_WEBHOOK_VALUE_TRACKER_INTEREST, {
    event: "value_tracker_interest",
    address,
    avm: avm || null,
    avmLow: avmLow || null,
    avmHigh: avmHigh || null,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
  });
}

// Fired from ValueTracker.js's saveTracker() the moment a homeowner
// successfully launches a NEW Value Tracker (isEdit === false — an edit to
// an existing tracker does NOT re-fire this). This is a logged-in-only
// event, so email/phone should always be populated from userDetails.
//
// `source` distinguishes a homeowner setting this up themselves
// ("self_setup") from one who arrived via an agent/lender's pre-filled
// invite link ("agent_invite") — handy if you want to see how much of your
// Value Tracker adoption is agent-driven vs. organic.
export function sendValueTrackerCreatedWebhook({
  firstName,
  lastName,
  email,
  phone,
  address,
  purchasePrice,
  mortgageBalance,
  currentAVM,
  currentEquity,
  source,   // "self_setup" | "agent_invite"
}) {
  return postToGhl(GHL_WEBHOOK_VALUE_TRACKER_CREATED, {
    event: "value_tracker_created",
    firstName,
    lastName,
    email: email || null,
    phone: phone || null,
    address,
    purchasePrice: purchasePrice || null,
    mortgageBalance: mortgageBalance || null,
    currentAVM: currentAVM || null,
    currentEquity: currentEquity || null,
    source: source || "self_setup",
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
  });
}

// Fired TWICE per signup, both to the same GHL Inbound Webhook URL, matched
// to the same Contact by email — this is what lets a single GHL automation
// branch on "did they confirm within N minutes":
//   1. From registrationform.js's onFinish(), right after Auth.signUp()
//      succeeds — accountConfirmed: false. This is the moment the workflow
//      should actually start.
//   2. From registrationform.js's handleOk(), right after
//      Auth.confirmSignUp() succeeds — accountConfirmed: true. This updates
//      the SAME Contact's "Account Confirmed" custom field so a later step
//      in the workflow (e.g. after a Wait step) sees the current state.
//
// GHL setup notes:
//   - Map `accountConfirmed` to a Contact custom field in the webhook
//     trigger's field mapping (send a sample payload first so GHL can
//     detect the field).
//   - Build the workflow as: Trigger → Wait (e.g. 5 min) → IF/ELSE on the
//     CONTACT's "Account Confirmed" field (not the Trigger/webhook payload
//     — that's a frozen snapshot from call #1 and will always read false).
//   - Leave "Allow re-entry" OFF on that workflow (the default). The second
//     webhook call (#2) still updates the Contact field even though
//     re-entry is off — it just won't spawn a second parallel workflow run
//     for the same person.
export function sendAccountCreatedWebhook({
  firstName,
  lastName,
  email,
  phone,
  userType,               // "homeowner" | "buyer" | "seller" | "agent" | whatever your signup form uses
  signupSource,           // where the signup happened — "public_signup", "agent_invite", "claim_home_flow", etc.
  referredByAgentName,    // optional — set when an agent invited this user
  referredByAgentId,      // optional
  address,                // optional — set if a home was already claimed pre-signup
  accountConfirmed,       // false on the signup-submit call, true on the confirm-code call
}) {
  return postToGhl(GHL_WEBHOOK_ACCOUNT_CREATED, {
    event: "account_created",
    firstName,
    lastName,
    email,
    phone,
    userType: userType || null,
    signupSource: signupSource || null,
    referredByAgentName: referredByAgentName || null,
    referredByAgentId: referredByAgentId || null,
    address: address || null,
    accountConfirmed: !!accountConfirmed,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    submittedAt: new Date().toISOString(),
  });
}